import * as React from 'react';
import BareContentEditableDivEditor from './BareContentEditableDivEditor';
import ReactEditor, { ReactEditorProps } from './ReactEditor';
import PlainTextEditor, { PlainTextEditorProps } from './PlainTextEditor';
import PreferredEditorToggle from './PreferredEditorToggle';
import setIsFocusWorkaroundDivShown from '../actions/setIsFocusWorkaroundDivShown';
import { observer } from 'mobx-react';
import { ComposeEditorType } from 'owa-mail-preferred-editor';
import { getActiveEditorType } from 'owa-mail-preferred-editor/lib/getActiveEditorType';
import { CompositeEditorViewState } from '../store/schema/CompositeEditorViewState';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isBrowserEdge, isBrowserIE } from 'owa-user-agent/lib/userAgent';

export interface CompositeEditorProps extends ReactEditorProps, PlainTextEditorProps {
    viewState: CompositeEditorViewState;
}

@observer
export default class CompositeEditor extends React.Component<CompositeEditorProps, {}> {
    private roosterEditor = React.createRef<ReactEditor>();
    private bareCEDEditor = React.createRef<BareContentEditableDivEditor>();
    private plainTextEditor = React.createRef<PlainTextEditor>();
    private originalOnFocus: () => void;
    private originalOnBlur: () => void;

    constructor(props: CompositeEditorProps) {
        super(props);
        this.originalOnFocus = props.onFocus;
        this.originalOnBlur = props.onBlur;
    }

    render() {
        const { viewState, classNames } = this.props;
        const props = {
            ...this.props,
            onFocus: this.onEditorFocus,
            onBlur: this.onEditorBlur,
        };
        const activeEditor = getActiveEditorType(viewState);
        let editor: JSX.Element = null;

        switch (activeEditor) {
            case ComposeEditorType.RoosterHTML:
                editor = <ReactEditor {...props} ref={this.roosterEditor} />;
                break;
            case ComposeEditorType.RawContentEditableDivHTML:
                editor = (
                    <BareContentEditableDivEditor
                        className={classNames}
                        viewState={viewState}
                        ref={this.bareCEDEditor}
                    />
                );
                break;
            case ComposeEditorType.Textarea:
                editor = <PlainTextEditor {...props} ref={this.plainTextEditor} />;
                break;
        }

        return (
            <>
                {this.renderFocusWorkaroundDiv()}
                {isFeatureEnabled('cmp-downgradeRawCED') && (
                    <PreferredEditorToggle
                        viewState={viewState}
                        getEditorContentFromDom={this.getEditorContentFromDom}
                    />
                )}
                {editor}
                {this.renderFocusWorkaroundDiv()}
            </>
        );
    }

    // Workaround a behavior in Edge/IE
    // Add an extra DIV before and after editor to accept the focus by press TAB/Shift+Tab then use code to trigger the real focus into editor
    // So that the view will not do auto scroll
    private renderFocusWorkaroundDiv(): JSX.Element {
        return (
            (isBrowserEdge() || isBrowserIE()) &&
            this.props.viewState.isFocusWorkaroundDivShown && (
                <div onFocus={this.onFocusToWorkaroundDiv} tabIndex={0} />
            )
        );
    }

    private onFocusToWorkaroundDiv = () => {
        if (this.roosterEditor.current) {
            this.roosterEditor.current.focus();
        } else if (this.plainTextEditor.current) {
            this.plainTextEditor.current.focus();
        }
    };

    private onEditorFocus = () => {
        setIsFocusWorkaroundDivShown(this.props.viewState, false /*isShown*/);
        if (this.originalOnFocus) {
            this.originalOnFocus();
        }
    };
    private onEditorBlur = () => {
        setIsFocusWorkaroundDivShown(this.props.viewState, true /*isShown*/);
        if (this.originalOnBlur) {
            this.originalOnBlur();
        }
    };

    private getEditorContentFromDom = () => {
        const editor = this.roosterEditor.current || this.bareCEDEditor.current;
        return editor ? editor.getContent() : null;
    };
}
